import {
    Button,
    FormGroup,
    FormLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { AppFormCell } from "../../components/AppFormCell/AppFormCell";
import { AppPage } from "../../components/Page/Page";
import { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { ApiServiceEndpoint } from "../../api/api.types";
import { ApiServiceContext } from "../../api/ApiService.context";
import { Text } from '@hubdevs/viu-ui-shared';
import { Bundle, BundleAttribution } from "../../api/bundle/types";
import { AppTableWithButtons } from "../../components/TableWithButtons/TableWithButtons";
import { AppFormRow } from "../../components/AppFormRow/AppFormRow";
import { AppTextField } from "../../components/AppTextField/AppTextField.component";
import { OrganizationLink } from "../../api/links/types";
import { OrganizationAttributionSummary } from "../../api/attribution/types";
import { LINK_ATTRIBUTION_COLUMNS, LinkAttributionRow } from "./linkAttributionColumns";
import { CopyToClipboardButton } from "../../components/CopyToClipboardButton/CopyToClipboardButton";
import './Link.page.css';
import { ApiServiceCollection } from "../../api/ApiServiceCollection.class";
import { LinkService } from "../../api/links/LinkService.class";
import { OrganizationService } from "../../api/organization/OrganizationService.class";
import { BundleService } from "../../api/bundle/BundleService.class";

const ATTRIBUTIONS_PAGE_SIZE = 100;
const BUNDLES_PAGE_SIZE = 100;

type LoadingState = 'NOT STARTED' | 'LOADING' | 'SUCCESS' | 'FAIL';

export const LinkPage = () => {
    const { organizationGuid, linkGuid } = useParams();
    const apiServiceContext = useContext(ApiServiceContext);
    const { LinkService, OrganizationService, BundleService } = apiServiceContext.getApiServiceCollections() as {
        LinkService: LinkService;
        OrganizationService: OrganizationService;
        BundleService: BundleService;
        [key: string]: ApiServiceCollection;
    };
    const navigate = useNavigate();

    const [linkError, setLinkError] = useState('');
    const [bundlesError, setBundlesError] = useState('');
    const [loadingState, setLoadingState] = useState<LoadingState>('NOT STARTED');
    const [bundlesLoadingState, setBundlesLoadingState] = useState<LoadingState>('NOT STARTED');
    const [routeName, setRouteName] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [bundleGuid, setBundleGuid] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [currentLink, setCurrentLink] = useState<OrganizationLink>({
        displayName: '',
        organizationGuid: organizationGuid || '',
        bundleGuid: '',
        linkGuid: linkGuid || '',
        routeName: '',
        bundleName: '',
        product: null,
        url: ''
    });
    const [bundles, setBundles] = useState<Bundle[]>([]);
    const [orgAttributions, setOrgAttributions] = useState<OrganizationAttributionSummary[]>([]);
    const [orgAttrsLoadingState, setOrgAttrsLoadingState] = useState<LoadingState>('NOT STARTED');
    const [orgAttrsError, setOrgAttrsError] = useState('');
    const [bundleAttributions, setBundleAttributions] = useState<BundleAttribution[]>([]);
    const [bundleAttrsLoadingState, setBundleAttrsLoadingState] = useState<LoadingState>('SUCCESS');
    const [bundleAttrsError, setBundleAttrsError] = useState('');
    const [quoteExperienceName, setQuoteExperienceName] = useState('');
    const [quoteExperienceGuid, setQuoteExperienceGuid] = useState('');
    const [quoteExperienceLoadingState, setQuoteExperienceLoadingState] = useState<LoadingState>('SUCCESS');
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
    const [displayNameError, setDisplayNameError] = useState('');
    const [routeNameError, setRouteNameError] = useState('');

    const canSave = !!displayName && !!routeName;

    useEffect(() => {
        const fetchLink = async() => {
            setLinkError('');

            if (!linkGuid) {
                // Create mode
                setLoadingState('SUCCESS');
                return;
            }

            setLoadingState('LOADING');
            let getLinkResp = await LinkService.getLinkById(linkGuid);
            while (getLinkResp.retry) {
                getLinkResp = await getLinkResp.retry.catch(error => error)
            }
            if (getLinkResp.error) {
                setLinkError('Failed to load selected link');
                setLoadingState('FAIL');
                return;
            }
            const linkData = getLinkResp.jsonBody!;
            if (!linkData) throw new Error();
            setCurrentLink(linkData);
            setBundleGuid(linkData.bundleGuid);
            setDisplayName(linkData.displayName);
            setRouteName(linkData.routeName);
            setLoadingState('SUCCESS');
        }
        fetchLink();
    }, [linkGuid, LinkService]);

    useEffect(() => {
        const fetchBundles = async () => {
            setBundlesError('');

            if (!organizationGuid) {
                setBundles([]);
                setBundlesLoadingState('SUCCESS');
                return;
            }

            setBundlesLoadingState('LOADING');
            let getBundlesResp = await BundleService.getBundles(
                { organizationGuid, skip: 0, limit: BUNDLES_PAGE_SIZE },
                { type: 'STANDARD' }
            );
            while (getBundlesResp.retry) {
                getBundlesResp = await getBundlesResp.retry.catch(error => error);
            }
            if (getBundlesResp.error) {
                setBundlesError('Failed to load organization bundles');
                setBundlesLoadingState('FAIL');
                return;
            }
            setBundles(getBundlesResp.jsonBody?.data || []);
            setBundlesLoadingState('SUCCESS');
        }
        fetchBundles();
    }, [organizationGuid, BundleService]);

    useEffect(() => {
        const fetchOrgAttrs = async () => {
            setOrgAttrsError('');

            if (!organizationGuid) {
                setOrgAttributions([]);
                setOrgAttrsLoadingState('SUCCESS');
                return;
            }

            setOrgAttrsLoadingState('LOADING');
            let getOrgAttrsResp = await OrganizationService.getOrganizationAttributions({
                organizationGuid,
                skip: 0,
                limit: ATTRIBUTIONS_PAGE_SIZE,
                query: { status: 'ACTIVE' },
            });
            while (getOrgAttrsResp.retry) {
                getOrgAttrsResp = await getOrgAttrsResp.retry.catch(error => error);
            }
            if (getOrgAttrsResp.error) {
                setOrgAttrsError('Failed to load organization attributions');
                setOrgAttrsLoadingState('FAIL');
                return;
            }
            setOrgAttributions(getOrgAttrsResp.jsonBody?.data || []);
            setOrgAttrsLoadingState('SUCCESS');
        }
        fetchOrgAttrs();
    }, [organizationGuid, OrganizationService]);

    useEffect(() => {
        if (!bundleGuid) {
            setBundleAttributions([]);
            setBundleAttrsError('');
            setBundleAttrsLoadingState('SUCCESS');
            return;
        }

        const fetchBundleAttrs = async () => {
            setBundleAttrsError('');
            setBundleAttrsLoadingState('LOADING');

            let getBundleAttrsResp = await BundleService.getAttributions(bundleGuid);
            while (getBundleAttrsResp.retry) {
                getBundleAttrsResp = await getBundleAttrsResp.retry.catch(error => error);
            }
            if (getBundleAttrsResp.error) {
                setBundleAttrsError('Failed to load bundle attributions');
                setBundleAttrsLoadingState('FAIL');
                return;
            }
            setBundleAttributions(getBundleAttrsResp.jsonBody || []);
            setBundleAttrsLoadingState('SUCCESS');
        }
        fetchBundleAttrs();
    }, [bundleGuid, BundleService]);

    useEffect(() => {
        if (!bundleGuid) {
            setQuoteExperienceName('');
            setQuoteExperienceGuid('');
            setQuoteExperienceLoadingState('SUCCESS');
            return;
        }

        const fetchBundle = async () => {
            setQuoteExperienceLoadingState('LOADING');


            let getBundleResp = await BundleService.getBundleById(bundleGuid);
            while (getBundleResp.retry) {
                getBundleResp = await getBundleResp.retry.catch(error => error);
            }
            if (getBundleResp.error) {
                setQuoteExperienceName('');
                setQuoteExperienceGuid('');
                setQuoteExperienceLoadingState('FAIL');
                return
            }
            const parentBundleGuid = (getBundleResp.jsonBody as Bundle)?.parentGuid;
            if (!parentBundleGuid) {
                setQuoteExperienceName('');
                setQuoteExperienceGuid('');
                setQuoteExperienceLoadingState('SUCCESS');
                return null;
            }
            let getParentBundleResp = await BundleService.getBundleById(parentBundleGuid)
            while (getParentBundleResp.retry) {
                getParentBundleResp = await getParentBundleResp.retry.catch(error => error);
            }
            if (getParentBundleResp.error) {
                setQuoteExperienceName('');
                setQuoteExperienceGuid('');
                setQuoteExperienceLoadingState('FAIL');
                return;
            }
            setQuoteExperienceName(getParentBundleResp.jsonBody?.displayName || '');
            setQuoteExperienceGuid(parentBundleGuid);
            setQuoteExperienceLoadingState('SUCCESS');
        }
        fetchBundle();
    }, [bundleGuid, BundleService]);

    const isPageLoading = useMemo(() => {
        const isBusy = (s: LoadingState) => s === 'NOT STARTED' || s === 'LOADING';
        return (
            isBusy(loadingState) ||
            isBusy(bundlesLoadingState) ||
            isBusy(orgAttrsLoadingState) ||
            isBusy(bundleAttrsLoadingState)
        );
    }, [loadingState, bundlesLoadingState, orgAttrsLoadingState, bundleAttrsLoadingState]);

    const attributionRows = useMemo<LinkAttributionRow[]>(() => {
        const linkAttrMap = orgAttributions
            .filter(attr => attr.required)
            .map(attr => ({
                rowKey: `org-${attr.organizationAttributionGuid}`,
                attributionGuid: attr.attributionGuid,
                displayName: attr.displayName,
                type: attr.type,
                source: 'ORGANIZATION',
            } as LinkAttributionRow))
            .reduce((acc, cur) => {
                acc[cur.type] = cur;
                return acc;
            }, {} as Record<string, LinkAttributionRow>);

        bundleAttributions.forEach(attr => {
            linkAttrMap[attr.type] = {
                rowKey: `bundle-${attr.organizationAttributionGuid}`,
                attributionGuid: attr.attributionGuid,
                displayName: attr.displayName,
                type: attr.type,
                source: 'BUNDLE',
            }
        });

        return Object.values(linkAttrMap);
    }, [orgAttributions, bundleAttributions]);

    const navigateBackToOrganizationPage = () => {
        navigate(`/organization/${organizationGuid}`);
    };

    const saveLink = () => {
        setLinkError('');
        setIsSaving(true);

        const linkEndpoint: ApiServiceEndpoint<any> = linkGuid
            ? LinkService.updateLink
            : LinkService.createLink;

        const selectedBundle = bundles.find(b => b.bundleGuid === bundleGuid);
        const bundleName = selectedBundle?.displayName || '';

        linkEndpoint({
            ...currentLink,
            displayName,
            routeName,
            bundleName,
            bundleGuid: bundleGuid || null,
        })
            .then((res) => {
                navigateBackToOrganizationPage();
            })
            .catch((e) => {
                const detail = e?.data?.detail || e?.detail;
                setLinkError(detail || 'Unknown Error occurred when saving link.');
                setLoadingState('FAIL');
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const handleBundleChange = (event: SelectChangeEvent<string>) => {
        setBundleGuid(event.target.value || null);
    };

    const handleCreateBundle = () => {
        if (!organizationGuid) return;
        navigate(`/organization/${organizationGuid}/bundles`);
    };


    const renderAttributions = () => {
        if (orgAttrsError) {
            return <Text>{orgAttrsError}</Text>;
        }
        return (
            <>
                <AppTableWithButtons
                    rowIdField="rowKey"
                    rows={attributionRows}
                    columns={LINK_ATTRIBUTION_COLUMNS}
                    title="Attributions"
                    pagination
                    totalRecords={attributionRows.length}
                    paginationMode="client"
                />
                {bundleAttrsError ? <Text>{bundleAttrsError}</Text> : null}
            </>
        );
    }

    const renderForm = () => (
        <>
            <AppFormRow>
                <AppFormCell>
                    <FormLabel htmlFor="linkName">Link Display Name</FormLabel>
                    <FormGroup>
                        <AppTextField
                            required
                            fullWidth
                            name="linkName"
                            value={displayName}
                            error={(hasAttemptedSubmit && !!displayNameError)}
                            helperText={displayNameError}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                setDisplayName(event.target.value);
                                if (displayNameError) setDisplayNameError('');
                            }}
                        />
                    </FormGroup>
                </AppFormCell>
            </AppFormRow>
            <AppFormRow>
                <AppFormCell>
                    <FormLabel htmlFor="routeName">Route Name</FormLabel>
                    <FormGroup row sx={{ gap: 2, alignItems: "center", flexWrap: "nowrap" }}>
                        <AppTextField
                            required
                            disabled={!!linkGuid}
                            name="routeName"
                            value={routeName}
                            error={(hasAttemptedSubmit && !!routeNameError)}
                            helperText={routeNameError}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                setRouteName(event.target.value);
                                if (routeNameError) setRouteNameError('');
                            }}
                        />
                        {currentLink.url && (
                            <div className="link-page-url-preview">
                                <Tooltip title={currentLink.url} placement="top">
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        noWrap
                                        sx={{ maxWidth: 300 }}
                                    >
                                        {currentLink.url}
                                    </Typography>
                                </Tooltip>
                                <CopyToClipboardButton text={currentLink.url} />
                            </div>
                        )}
                    </FormGroup>
                </AppFormCell>
            </AppFormRow>
        </>
    );

    const quoteExperiencePath = quoteExperienceGuid
        ? `/organization/${organizationGuid}/quote-experience/${quoteExperienceGuid}`
        : null;

    const renderQuoteExperienceValue = () => {
        if (quoteExperienceLoadingState === 'LOADING') {
            return <Typography variant="body2" color="text.secondary">Loading…</Typography>;
        }

        if (quoteExperienceLoadingState === 'FAIL') {
            return (
                <Typography variant="body2" color="error">
                    Could not load.
                </Typography>
            );
        }

        if (quoteExperienceName && quoteExperiencePath) {
            return (
                <RouterLink
                    to={quoteExperiencePath}
                    className="link-page-qe-link"
                >
                    {quoteExperienceName}
                    <OpenInNewIcon fontSize="inherit" />
                </RouterLink>
            );
        }

        return (
            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                None
            </Typography>
        );
    };

    const renderBundleAndQuoteExperience = () => (
        <>
            <AppFormRow>
                <AppFormCell>
                    <FormLabel htmlFor="bundle-select">Bundle</FormLabel>
                    <FormGroup row sx={{ gap: 2, alignItems: "center", flexWrap: "nowrap" }}>
                        <Select
                            value={bundleGuid || ""}
                            onChange={handleBundleChange}
                            displayEmpty
                            id="bundle-select"
                            aria-labelledby="bundle-label"
                            sx={{ minWidth: 240 }}
                        >
                            <MenuItem value="">None</MenuItem>
                            {bundles.map((bundle, index) => (
                                <MenuItem key={`${bundle.bundleGuid}-${index}`} value={bundle.bundleGuid}>
                                    {bundle.displayName}
                                </MenuItem>
                            ))}
                        </Select>
                        <Button
                            variant="text"
                            size="small"
                            startIcon={<AddIcon />}
                            disabled={!organizationGuid}
                            onClick={handleCreateBundle}
                            sx={{ whiteSpace: "nowrap" }}
                        >
                            Create new bundle
                        </Button>
                    </FormGroup>
                </AppFormCell>
            </AppFormRow>
            {bundleGuid && (
                <AppFormRow>
                    <AppFormCell>
                        <div className="link-page-qe-row">
                            <Typography variant="body1" component="span">
                                Quote Experience:
                            </Typography>
                            {renderQuoteExperienceValue()}
                        </div>
                    </AppFormCell>
                </AppFormRow>
            )}
        </>
    );

    function getFormDisplay() {
        return (
            <div className="link-page-form">
                {renderForm()}
                {bundlesError ? <Text>{bundlesError}</Text> : null}
                {renderBundleAndQuoteExperience()}
                {renderAttributions()}
                {loadingState === 'FAIL' ? <Text>{linkError}</Text> : null}
            </div>
        );
    }


    return (
        <AppPage
            title={linkGuid ? "Edit Link" : "Create New Link"}
            isLoading={isPageLoading}
            buttonRowContent={
                <>
                    <AppFormCell>
                        <Button variant="outlined" onClick={navigateBackToOrganizationPage}>Cancel</Button>
                    </AppFormCell>
                    {
                        (loadingState === 'SUCCESS' || loadingState === 'FAIL') && (
                            <AppFormCell>
                                <Button
                                    disabled={!canSave || isSaving}
                                    variant="contained"
                                    onClick={() => {
                                        setHasAttemptedSubmit(true);
                                        if (canSave) saveLink();
                                    }}
                                >{isSaving ? 'Saving...' : 'Save'}</Button>
                            </AppFormCell>
                        )
                    }
                </>
            }
        >
            {getFormDisplay()}
        </AppPage>
    );
};
